import whenIsNotNil from "@gef-ui/utils/whenIsNotNil";
import debounce from '@material-ui/core/utils/debounce';
import PropTypes from 'prop-types';
import always from "ramda/es/always";
import head from "ramda/es/head";
import indexOf from "ramda/es/indexOf";
import lte from "ramda/es/lte";
import multiply from "ramda/es/multiply";
import o from "ramda/es/o";
import pick from "ramda/es/pick";
import sort from "ramda/es/sort";
import times from "ramda/es/times";
import when from "ramda/es/when";
import alwaysZero from "ramda-extension/es/alwaysZero";
import cx from "ramda-extension/es/cx";
import defaultToEmptyObject from "ramda-extension/es/defaultToEmptyObject";
import defaultToZero from "ramda-extension/es/defaultToZero";
import isNegative from "ramda-extension/es/isNegative";
import isNotNil from "ramda-extension/es/isNotNil";
import React, { Children, cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import HorizontalScrollButton, { buttonPositions } from '../HorizontalScrollButton';
const validateScrollValue = (maxValue)=>o(when(lte(maxValue), always(maxValue)), when(isNegative, alwaysZero));
const pickNodeMetadata = pick([
    'clientWidth',
    'scrollLeft',
    'scrollTop',
    'scrollWidth',
    'offsetWidth'
]);
const getNodeMetadata = o(defaultToEmptyObject, whenIsNotNil(pickNodeMetadata));
const getActiveElementOffsetLeft = (elementId)=>document.getElementById(elementId).offsetLeft || 0;
const getActiveElementBoundingRect = (elementId)=>document.getElementById(elementId).getBoundingClientRect();
const getClosestActivePointer = (left)=>o(head, sort((a, b)=>Math.abs(a - left) - Math.abs(b - left)));
export const ACTIVE_ITEM = 'HorizontalScroll--active';
export const usePointersUpdater = ({ setScrollPointer, setScrollPosition, activeIndex, scrollLeftRef, id, scrollStart })=>{
    const updateAndGetPointerState = useCallback(()=>{
        const { scrollWidth, clientWidth } = getNodeMetadata(scrollLeftRef.current);
        const totalNumberOfScroll = defaultToZero(Math.ceil(scrollWidth / clientWidth));
        const recalculateScrollValue = validateScrollValue(scrollWidth + clientWidth);
        const getScrollOptions = times(o(recalculateScrollValue, multiply(clientWidth)));
        setScrollPointer(getScrollOptions(totalNumberOfScroll));
        if (scrollStart > totalNumberOfScroll - 1) {
            setScrollPosition(({ scrollOffset })=>({
                    scrollOffset,
                    scrollStart: totalNumberOfScroll - 1
                }));
        }
        return getScrollOptions(totalNumberOfScroll);
    }, [
        setScrollPosition,
        scrollLeftRef,
        setScrollPointer,
        scrollStart
    ]);
    const updatePositions = useCallback(()=>{
        const newScrollPointer = updateAndGetPointerState();
        if (isNotNil(activeIndex)) {
            const offsetLeft = getActiveElementOffsetLeft(`${ACTIVE_ITEM}${id}`);
            const closest = getClosestActivePointer(offsetLeft)(newScrollPointer);
            const { left: activeLeft, right: activeRight } = getActiveElementBoundingRect(`${ACTIVE_ITEM}${id}`);
            const { left, right } = scrollLeftRef.current.getBoundingClientRect();
            // only update if active item is partially hidden or scrollStart is invalid
            if (activeRight > right || activeLeft < left || scrollStart + 1 > newScrollPointer.length) {
                setScrollPosition({
                    scrollOffset: -(closest - offsetLeft),
                    scrollStart: indexOf(closest, newScrollPointer)
                });
            }
        }
    }, [
        activeIndex,
        scrollLeftRef,
        id,
        setScrollPosition,
        updateAndGetPointerState,
        scrollStart
    ]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: if updatePositions is included, the effect runs twice
    useEffect(()=>{
        debounce(()=>{
            updatePositions();
        })();
    }, [
        activeIndex
    ]);
    // if tabs have dynamic width, we need to update when resized
    const [previousWidth, setPreviousWidth] = useState(0);
    useEffect(()=>{
        const activeItemResizeObserver = new ResizeObserver((entries)=>{
            const newWidth = Math.ceil(entries[0].contentRect.width);
            setPreviousWidth(newWidth);
            // only run update if the item was actually resized
            if (previousWidth !== newWidth) {
                debounce(()=>{
                    updatePositions();
                })();
            }
        });
        const activeItem = document.getElementById(`${ACTIVE_ITEM}${id}`);
        if (activeItem) {
            activeItemResizeObserver.observe(activeItem);
        }
        return ()=>{
            activeItemResizeObserver.disconnect();
        };
    }, [
        previousWidth,
        updatePositions,
        id
    ]);
    useEffect(()=>{
        const scrollLeftResizeObserver = new ResizeObserver(()=>{
            window.requestAnimationFrame(()=>{
                updateAndGetPointerState();
            });
        });
        scrollLeftResizeObserver.observe(scrollLeftRef.current);
        return ()=>{
            scrollLeftResizeObserver.disconnect();
        };
    }, [
        updateAndGetPointerState,
        scrollLeftRef
    ]);
};
export const useHorizontalScrollState = (activeIndex, id)=>{
    const [{ scrollStart, scrollOffset }, setScrollPosition] = useState({
        scrollStart: 0,
        scrollOffset: 0
    });
    const [scrollPointer, setScrollPointer] = useState([]);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const scrollLeftRef = useRef();
    usePointersUpdater({
        setScrollPointer,
        setScrollPosition,
        activeIndex,
        scrollLeftRef,
        id,
        scrollStart
    });
    const handleScrollClick = useCallback((rtl)=>{
        const delta = rtl ? -1 : 1;
        setScrollPosition({
            scrollOffset: 0,
            // Do not decrement scrollStart if scrollOffset has a value
            // (implies that a tab was scrolled into view when clicked and decrementing might skip some tabs)
            scrollStart: scrollOffset > 0 && rtl ? scrollStart : validateScrollValue(scrollPointer.length - 1)(scrollStart + delta)
        });
    }, [
        scrollOffset,
        scrollStart,
        scrollPointer.length
    ]);
    // moved here from ScrollLeft component - scrollLeft of ref wasn't getting updated correctly
    useEffect(()=>{
        if (scrollLeftRef.current) {
            const scrollLeft = scrollPointer[scrollStart] + scrollOffset;
            scrollLeftRef.current.scrollLeft = scrollLeft;
            const { scrollWidth, offsetWidth } = getNodeMetadata(scrollLeftRef.current);
            setShowRightArrow(scrollPointer.length > 1 ? scrollLeft + offsetWidth < scrollWidth : false);
        }
    }, [
        scrollStart,
        scrollOffset,
        scrollPointer
    ]);
    return {
        handleScrollClick,
        scrollOffset,
        scrollStart,
        scrollPointer,
        scrollLeftRef,
        showRightArrow
    };
};
const HorizontalScroll = ({ activeIndex, children, className, id })=>{
    const finalId = useMemo(()=>id ? id : crypto.randomUUID(), [
        id
    ]);
    const { handleScrollClick, scrollOffset, scrollStart, scrollPointer, scrollLeftRef, showRightArrow } = useHorizontalScrollState(activeIndex, finalId);
    const { scrollWidth, offsetWidth } = getNodeMetadata(scrollLeftRef.current);
    const renderEnhancedChildren = ()=>Children.map(children, (child, index)=>{
            if (!/*#__PURE__*/ isValidElement(child)) {
                return null;
            }
            const isActive = index === activeIndex;
            return /*#__PURE__*/ cloneElement(child, {
                key: index,
                isActive: always(isActive),
                id: isActive ? `${ACTIVE_ITEM}${finalId}` : undefined
            });
        });
    const scrollLeftValue = scrollPointer[scrollStart] + scrollOffset;
    return /*#__PURE__*/ React.createElement("div", {
        className: cx('HorizontalScroll', className)
    }, /*#__PURE__*/ React.createElement(HorizontalScrollButton, {
        handleClick: ()=>handleScrollClick(true),
        position: buttonPositions.right,
        // only show button if scroll div is overflowing and is scrolled right
        visible: offsetWidth < scrollWidth && scrollLeftValue > 0
    }), /*#__PURE__*/ React.createElement("div", {
        ref: scrollLeftRef,
        className: 'HorizontalScroll-base',
        style: {
            scrollBehavior: 'smooth'
        }
    }, renderEnhancedChildren()), /*#__PURE__*/ React.createElement(HorizontalScrollButton, {
        handleClick: ()=>handleScrollClick(false),
        position: buttonPositions.left,
        visible: showRightArrow
    }));
};
HorizontalScroll.propTypes = {
    /**
	 * Used to handle scrolling active item into view
	 */ activeIndex: PropTypes.number,
    /**
	 * Internally, every children receives `isActive` function as prop when activeIndex and item index matches
	 */ children: PropTypes.node,
    /**
	 * Root element css class property
	 */ className: PropTypes.string,
    /**
	 * In case there is more than one instance of this Component on page you can use this to differentiate them
	 */ id: PropTypes.string
};
export default HorizontalScroll;
